/**
 * Documentation on this component can be found at
 * https://github.com/webflow/webflow/blob/dev/docs/app/color-picker/color-picker.md
 */

import type {
  SwatchType,
  ColorValue,
} from '@packages/systems/core/utils/StyleTypes';

import {EnhancedScrollView} from '../ScrollView';
import type {OnChangeMeta} from '../WebflowUnitInput';
import {isEqual, noop} from 'lodash';

import * as React from 'react';
import {emptyObject} from '@packages/utilities/fp/utils';
import {weakMemo} from '@packages/utilities/memo';
import {
  AddIcon,
  Button as SpringButton,
  tokens,
  Note,
  View,
  HideIcon,
  ShowIcon,
  HelpCircleIcon,
  EyedropperMediumIcon,
  ViewEdgesIcon,
  Link,
  NoteLink,
  HStack,
} from '@packages/systems/spring';
import {
  parseColor,
  getHSVA,
  fromHue,
  getHex,
  toRgb,
  humanizeNamedColor,
  closestColorName,
  isValidColor,
  hsvToString,
  hsvToHsl,
  hslToHsv,
  isTransparent,
  isPartiallyTransparent,
  generateContrastRatioLinePath,
  blendColors,
  type HSVA,
} from '@packages/systems/core/utils/ColorUtils';

import {
  styled,
  styledDiv,
  designerTheme,
  emotionStyled,
} from '@packages/utilities/styled';
import {translate, list, shadow} from '@packages/utilities/inline-style-utils';
import {oneLineText} from '../constants/Styles';
import {
  changeSwatchColor,
  createNewSwatch,
  generateSwatchGuid,
  cancelSwatchEdit,
  saveSwatchEdit,
  deleteSwatch,
  LIGHT_CHECKERBOARD_ICON_BACKGROUND_POSITION,
  LIGHT_CHECKERBOARD_ICON_BACKGROUND_URL,
  generateLightCheckeredBackgroundImage,
} from '@packages/systems/style-legacy/swatches';

import draggable from '../logic/draggable';
import * as picker from './picker';
import * as slider from '../logic/slider';

import {BaseButton as Button} from '../Button';
import Hitarea from '../Hitarea';
import {Clickable} from '../logic/clickable';
import {Locator} from '@packages/systems/ui/overlay';
import NumberInput from '../WebflowNumberInput';
import PseudoProvider from '../PseudoProvider';
// eslint-disable-next-line webflow/no-dangerous-imports
import DragGhost from '@packages/systems/ui/shared-ui/DragGhost';

import {Input} from '../Input';
import TooltipTrigger from '../TooltipTrigger';
import {Copy} from '../Text';
import {enhanceInput} from './ColorInputField';

import {SwatchNib as CornerNib} from '@packages/systems/ui/icons';
import {TransparentCheckerboardDark as DarkCheckerboardIcon} from '@packages/systems/ui/icons/style-panel';

import {reportBug} from '@packages/utilities/reporter';

import tinycolor from 'tinycolor2';
import {FlexRow} from '../Layout';
import {Translation} from '@packages/systems/designer-translations';
import {Point} from 'bezier-js';

type ColorString = string;
type OnChange = (value: ColorString | HSVA, meta: OnChangeMeta) => void;

type ColorPickerPropType = {
  value: ColorString;
  onChange: OnChange;
  analyzeColorAccessibility?: boolean;
  showColorContrastLines: boolean;
  getNativeElement: () => HTMLElement | null;
};
type HSVColorPickerPropType = {
  value: HSVA;
  onChange: OnChange;
};

const Divider = emotionStyled('div')({
  gridColumn: '1 / -1',
  height: 1,
  background: tokens.colors.border1,
});

/*
 * ----------------------------------------------------------------------------
 * Saturation / Brightness ColorBox Picker
 * ----------------------------------------------------------------------------
 */
const CIRCLE_DIMENSION = 12;
const COLOR_BOX_HEIGHT = 150;

const CirclePicker = styledDiv(
  ({
    theme: {
      control,
      WebflowColorPicker: {borderColor},
    },
  }) => ({
    position: 'absolute',
    borderRadius: '50%',
    boxSizing: 'border-box',
    border: `${control.borderWidth * 2}px solid white`,
    width: CIRCLE_DIMENSION,
    height: CIRCLE_DIMENSION,
    boxShadow: list(
      shadow('inset', 0, 0, 0, 1, borderColor),
      shadow(0, 0, 0, 1, borderColor)
    ),
  })
);

const ColorBoxStyle = draggable(
  styledDiv(({value, width, theme: {control, colors}}) => ({
    position: 'relative',
    boxSizing: 'border-box',
    width,
    height: COLOR_BOX_HEIGHT,
    boxShadow: `0 0 0 ${control.borderWidth}px ${colors.borderDeep}`,
    borderRadius: control.borderRadius,
    background: `
     linear-gradient(rgba(0, 0, 0, 0), black),
      linear-gradient(to right, white, rgba(255, 255, 255, 0)) ${fromHue(
        value.h
      )}
    `,
    cursor: 'crosshair',
  }))
);

const changeStartRef = {changeType: 'changeStart'} as const;
const changeRef = {changeType: 'change'} as const;
const changeEndRef = {changeType: 'changeEnd'} as const;

type ColorBoxProps = HSVColorPickerPropType & {
  width: number;
  showContrastLines?: boolean;
  getNativeElement: () => HTMLElement | null;
};

type SliderState = {
  value: number;
  min: number;
  max: number;
  step: number;
  knobSize: number;
  length: number;
  vertical: boolean;
  reverse: boolean;
  dragging: boolean;
  _value: HSVA;
};

type ColorBoxState = {
  x: SliderState;
  y: SliderState;
  _value: HSVA;
};

class ColorBox extends React.PureComponent<ColorBoxProps, ColorBoxState> {
  // @ts-expect-error - TS2416 - Property 'state' in type 'ColorBox' is not assignable to the same property in base type 'PureComponent<ColorBoxProps, ColorBoxState, any>'.
  state = picker.getInitialState({
    x: {
      value: this.props.value.s,
      max: 1,
      step: 0,
    },
    y: {
      value: this.props.value.v,
      max: 1,
      step: 0,
      length: COLOR_BOX_HEIGHT,
      vertical: true,
      reverse: true,
    },
    _value: this.props.value,
  });

  static getDerivedStateFromProps(props: ColorBoxProps, state: ColorBoxState) {
    let newState = state;

    if (props.width !== state.x.length) {
      newState = {...newState, x: {...newState.x, length: props.width}};
    }
    if (!isEqual(props.value, newState._value)) {
      newState = {
        ...newState,
        _value: props.value,
        x: {
          ...newState.x,
          value: props.value.s,
        },
        y: {
          ...newState.y,
          value: props.value.v,
        },
      };
    }

    return newState !== state ? newState : null;
  }

  getHSV() {
    const {
      x: {value: xValue},
      y: {value: yValue},
    } = this.state;
    // @ts-expect-error - TS2339 - Property '_value' does not exist on type '{ x: any; y: any; }'.
    const {h, a} = this.state._value;

    // if a is 0, then make it 1 so that the user can see the color change. Otherwise
    // the UX is a bit confusing.
    return {h, a: a || 1, s: xValue, v: yValue};
  }

  onDragStart = (coords: Point) => {
    this.setState(
      (state) => picker.pickerDragStart(state, coords),
      () => {
        this.props.onChange(this.getHSV(), changeStartRef);
      }
    );
  };

  onDrag = (coords: Point) => {
    this.setState(
      (state) => picker.pickerDrag(state, coords),
      () => {
        this.props.onChange(this.getHSV(), changeRef);
      }
    );
  };

  onDragEnd = () => {
    this.setState(picker.pickerDragEnd, () => {
      this.props.onChange(this.getHSV(), changeEndRef);
    });
  };

  render() {
    const state = this.state;
    const {showContrastLines, getNativeElement} = this.props;

    return (
      <ColorBoxStyle
        data-automation-id="colorBox"
        mode="relative"
        // @ts-expect-error - TS2769 - No overload matches this call. | TS2339 - Property '_value' does not exist on type '{ x: any; y: any; }'.
        value={this.state._value}
        width={state.x.length}
        dragging={state.x.dragging}
        onDragStart={this.onDragStart}
        onDrag={this.onDrag}
        onDragEnd={this.onDragEnd}
      >
        <DragGhost dragging={state.x.dragging} cursor="crosshair" />
        {showContrastLines && (
          <AccessibleColorLineOverlay
            // @ts-expect-error - TS2339 - Property '_value' does not exist on type '{ x: any; y: any; }'.
            currentColor={this.state._value}
            getNativeElement={getNativeElement}
          />
        )}

        <CirclePicker
          style={{
            top: -CIRCLE_DIMENSION / 2,
            left: -CIRCLE_DIMENSION / 2,
            transform: translate(
              Math.round(slider.getKnobPosition(state.x) || 0),
              Math.round(slider.getKnobPosition(state.y) || 0),
              0
            ),
          }}
        />
      </ColorBoxStyle>
    );
  }
}

// @ts-expect-error - TS7031 - Binding element 'currentColor' implicitly has an 'any' type. | TS7031 - Binding element 'getNativeElement' implicitly has an 'any' type.
const AccessibleColorLineOverlay = ({currentColor, getNativeElement}) => {
  const element = React.useMemo(() => getNativeElement(), [getNativeElement]);
  if (element == null) return null;

  const backgroundColor = getBackgroundColorFromElement(element);
  const wcagFontSize = getWcagFontSizeFromElement(element);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      style={{
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
      }}
      data-automation-id="color-contrast-lines"
    >
      <path
        // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string | undefined'.
        d={generateContrastRatioLinePath(
          currentColor,
          backgroundColor,
          224,
          150,
          wcagFontSize,
          'aa'
        )}
        style={{
          fill: 'none',
          stroke: 'white',
          opacity: '0.7',
          strokeWidth: '1.5px',
        }}
      />
      <path
        // @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'string | undefined'.
        d={generateContrastRatioLinePath(
          currentColor,
          backgroundColor,
          224,
          150,
          wcagFontSize,
          'aaa'
        )}
        style={{
          fill: 'none',
          stroke: 'white',
          opacity: '0.7',
          strokeWidth: '1.5px',
        }}
      />
    </svg>
  );
};

/*
 * ----------------------------------------------------------------------------
 * Hue & Opacity Slider Picker
 * ----------------------------------------------------------------------------
 */

const showEyeDropper = function hasNativeEyeDropperSupport() {
  // @ts-expect-error - TS2339 - Property 'EyeDropper' does not exist on type 'Window & typeof globalThis'.
  return window.EyeDropper != null;
};

const KNOB_WIDTH = 6;
const KNOB_TOTAL_BORDER_WIDTH = 2;
const getEyeDropperWidth = () => (showEyeDropper() ? 32 : 0);
const HUE_OPACITY_GAP = 8;

const calculateSliderWidth = (width: number) =>
  width -
  getEyeDropperWidth() -
  (showEyeDropper() ? HUE_OPACITY_GAP : 0) -
  KNOB_WIDTH -
  KNOB_TOTAL_BORDER_WIDTH;

const Knob = styledDiv(({theme: {shadows, control}}) => ({
  position: 'absolute',
  height: 'calc(100% - 2px)',
  top: 1,
  width: KNOB_WIDTH,
  borderRadius: control.borderWidth,
  boxShadow: shadows.selected,
}));

const HueOpacityGrid = styledDiv(({theme: {gutters}}) => ({
  display: 'grid',
  gridTemplateColumns: showEyeDropper()
    ? `${getEyeDropperWidth()}px 1fr`
    : '1fr',
  gridTemplateRows: `${gutters.x4 * 3}px ${gutters.x4 * 3}px`,
  gridGap: HUE_OPACITY_GAP,
}));

const HueSliderStyle = draggable(
  styledDiv(({theme: {colors, control}}) => ({
    position: 'relative',
    boxSizing: 'border-box',
    boxShadow: `0 0 0 ${control.borderWidth}px ${colors.borderDeep}`,
    borderRadius: control.borderRadius,
    background:
      'linear-gradient(to left, #f00, #f0f, #00f, #0ff, #0f0, #ff0, #f00)',
    cursor: 'crosshair',
  }))
);

type HueSliderProps = HSVColorPickerPropType & {
  width: number;
};

class HueSlider extends React.PureComponent<HueSliderProps, SliderState> {
  state = slider.getInitialState({
    value: this.props.value.h,
    min: 0,
    max: 359,
    step: 1,
  });

  static getDerivedStateFromProps(props: HueSliderProps, state: SliderState) {
    const length = calculateSliderWidth(props.width);

    let newState = state;

    if (length !== state.length) {
      newState = {
        ...state,
        length,
      };
    }

    if (!isEqual(props.value, state._value)) {
      newState = {
        ...state,
        _value: props.value,
        value: props.value.h,
      };
    }

    return newState !== state ? newState : null;
  }

  getHSV = () => {
    const {
      value: h,
      _value: {s, v, a},
    } = this.state;
    return {h, s, v, a};
  };

  onDragStart = (coords: Point) => {
    this.setState(
      (state) => slider.dragStart(state, coords),
      () => {
        this.onChange(this.getHSV(), changeStartRef);
      }
    );
  };

  onDrag = (coords: Point) => {
    this.setState(
      (state) => slider.drag(state, coords),
      () => {
        this.onChange(this.getHSV(), changeRef);
      }
    );
  };

  onChange = (
    color: {
      a: number;
      h: number;
      s: number;
      v: number;
    },
    // @ts-expect-error - TS7006 - Parameter 'ref' implicitly has an 'any' type.
    ref
  ) => {
    if (this.props.onChange) {
      this.props.onChange(color, ref);
    }
  };

  onDragEnd = () => {
    this.setState(slider.dragEnd, () => {
      this.onChange(this.getHSV(), changeEndRef);
    });
  };

  render() {
    const state = this.state;

    return (
      <HueSliderStyle
        dragging={state.dragging}
        onDragStart={this.onDragStart}
        onDrag={this.onDrag}
        onDragEnd={this.onDragEnd}
        mode="relative"
      >
        <DragGhost dragging={state.dragging} cursor="crosshair" />

        <Knob style={{left: slider.getKnobPosition(state) || 0}} />
      </HueSliderStyle>
    );
  }
}

const OpacitySliderStyle = draggable(
  styledDiv(({theme: {colors, control}, value}) => {
    return {
      position: 'relative',
      boxSizing: 'border-box',
      boxShadow: `0 0 0 ${control.borderWidth}px ${colors.borderDeep}`,
      borderRadius: control.borderRadius,
      background: `linear-gradient(to left, ${hsvToString({
        ...value,
        a: 1,
      })} 0%, transparent 100%) no-repeat local 0% 0%, url('${LIGHT_CHECKERBOARD_ICON_BACKGROUND_URL}') ${LIGHT_CHECKERBOARD_ICON_BACKGROUND_POSITION} repeat local`,
      cursor: 'crosshair',
    };
  })
);

type OpacitySliderProps = HSVColorPickerPropType & {
  width: number;
};

class OpacitySlider extends React.PureComponent<
  OpacitySliderProps,
  SliderState
> {
  state = slider.getInitialState({
    value: this.props.value.a,
    min: 0,
    max: 1,
    step: 0,
  });

  static getDerivedStateFromProps(
    props: OpacitySliderProps,
    state: SliderState
  ) {
    const length = calculateSliderWidth(props.width);

    let newState = state;

    if (length !== state.length) {
      newState = {
        ...newState,
        length,
      };
    }

    if (!isEqual(props.value, state._value)) {
      newState = {
        ...newState,
        _value: props.value,
        value: props.value.a,
      };
    }

    return newState !== state ? newState : null;
  }

  getHSV = () => {
    const {
      value: a,
      _value: {h, s, v},
    } = this.state;
    return {h, v, s, a};
  };

  onDragStart = (coords: Point) => {
    this.setState(
      (state) => slider.dragStart(state, coords),
      () => {
        this.props.onChange(this.getHSV(), changeStartRef);
      }
    );
  };

  onDrag = (coords: Point) => {
    this.setState(
      (state) => slider.drag(state, coords),
      () => {
        this.props.onChange(this.getHSV(), changeRef);
      }
    );
  };

  onDragEnd = () => {
    this.setState(slider.dragEnd, () => {
      this.props.onChange(this.getHSV(), changeEndRef);
    });
  };

  render() {
    const state = this.state;

    return (
      <OpacitySliderStyle
        value={state._value}
        dragging={state.dragging}
        onDragStart={this.onDragStart}
        onDrag={this.onDrag}
        onDragEnd={this.onDragEnd}
        mode="relative"
      >
        <DragGhost dragging={state.dragging} cursor="crosshair" />

        <Knob style={{left: slider.getKnobPosition(state) || 0}} />
      </OpacitySliderStyle>
    );
  }
}

const ColorPickerGrid = styledDiv(({theme: {gutters}}) => ({
  display: 'grid',
  gridRowGap: gutters.x4 * 2,
}));

/*
 * ----------------------------------------------------------------------------
 * Inputs
 * ----------------------------------------------------------------------------
 */

const InputGrid = styledDiv(({theme: {gutters}}) => ({
  display: 'grid',
  gridColumn: '1 / -1',
  gridTemplateColumns: `5fr 10fr 3fr`,
  gridGap: gutters.x4 * 2,
}));

const RGBHSBGrid = styledDiv(({theme: {gutters}}) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(3, 1fr)`,
  gridColumnGap: gutters.x4,
}));

const RGBHSBButton = emotionStyled(SpringButton)({
  paddingLeft: 0,
  paddingRight: 0,
  [SpringButton.__experimentalTokens.ButtonContentViewWidth]: '100%',
});

const disallowTextDragging = (e: React.MouseEvent<HTMLInputElement>) =>
  e.preventDefault();

const HexInput = enhanceInput(
  // @ts-expect-error - TS2345 - Argument of type '({ focus, invalid, value, placeholder, invalidValue, onBlur, onChange, onFocus: _onFocus, onKeyDown, }: { focus: any; invalid: any; value: any; placeholder: any; invalidValue: any; onBlur: any; onChange: any; onFocus: any; onKeyDown: any; }) => Element' is not assignable to parameter of type '(props: unknown) => any'.
  ({
    focus,
    invalid,
    value,
    placeholder,
    invalidValue,
    onBlur,
    onChange,
    onFocus: _onFocus,
    onKeyDown,
  }) => {
    const onFocus = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select();
        _onFocus();
      },
      [_onFocus]
    );
    let colorOrName;

    if (invalidValue === '') {
      colorOrName = '';
    } else {
      colorOrName = invalidValue || value;
    }

    return (
      <Input
        data-automation-id="hex-color-input"
        focus={focus}
        invalid={invalid}
        value={colorOrName}
        placeholder={placeholder}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onDragStart={disallowTextDragging}
        onDrop={disallowTextDragging}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
      />
    );
  }
);

const TextCopy = styled(Copy)({
  placeSelf: 'center',
  userSelect: 'none',
});

type HSBInputsState = {
  h: number;
  s: number;
  v: number;
};

class HSBInputs extends React.PureComponent<
  HSVColorPickerPropType,
  HSBInputsState
> {
  onHChange = (value: number, changeType: OnChangeMeta) => {
    this.props.onChange(
      {
        ...this.props.value,
        h: Number(value),
      },
      changeType
    );
  };

  onSChange = (value: number, changeType: OnChangeMeta) => {
    this.props.onChange(
      {
        ...this.props.value,
        s: Number(value) / 100,
      },
      changeType
    );
  };

  onVChange = (value: number, changeType: OnChangeMeta) => {
    this.props.onChange(
      {
        ...this.props.value,
        v: Number(value) / 100,
      },
      changeType
    );
  };

  render() {
    const {h, s, v} = this.props.value;

    return (
      <>
        <NumberInput
          data-automation-id="hsva-red-input"
          value={Math.round(h)}
          min={0}
          max={359}
          step={1}
          onChange={this.onHChange}
        />
        <NumberInput
          data-automation-id="hsva-green-input"
          value={Math.round(s * 100)}
          min={0}
          max={100}
          step={1}
          onChange={this.onSChange}
        />
        <NumberInput
          data-automation-id="hsva-blue-input"
          value={Math.round(v * 100)}
          min={0}
          max={100}
          step={1}
          onChange={this.onVChange}
        />
      </>
    );
  }
}

type RGBInputsState = {
  r: number;
  g: number;
  b: number;
};

class RGBInputs extends React.PureComponent<
  HSVColorPickerPropType,
  RGBInputsState
> {
  state = (() => {
    const {r, g, b} = toRgb(hsvToString(this.props.value));
    return {r, g, b};
  })();

  onKeyUp = (e: any) => {
    if (e.shiftKey) {
      return;
    }

    const newColor = parseColor({
      ...toRgb(hsvToString(this.props.value)),
      ...this.state,
    });

    if (newColor) {
      this.props.onChange && this.props.onChange(newColor, changeEndRef);
    }
  };

  onKeyDown = () => {
    const newColor = parseColor({
      ...toRgb(hsvToString(this.props.value)),
      ...this.state,
    });

    if (newColor) {
      this.props.onChange && this.props.onChange(newColor, changeStartRef);
    }
  };

  getState = () => {
    if (this.props.value != null) {
      const {r, g, b} = toRgb(hsvToString(this.props.value));
      return {r, g, b};
    }

    return this.state;
  };

  onRChange = (value: number, changeType: OnChangeMeta) => {
    this.setState(
      {r: Number(value)},
      this.props.onChange
        ? () => {
            const newColor = parseColor({
              ...toRgb(hsvToString(this.props.value)),
              r: this.state.r,
            });

            newColor && this.props.onChange(newColor, changeType);
          }
        : undefined
    );
  };

  onGChange = (value: number, changeType: OnChangeMeta) => {
    this.setState(
      {g: Number(value)},
      this.props.onChange
        ? () => {
            const newColor = parseColor({
              ...toRgb(hsvToString(this.props.value)),
              g: this.state.g,
            });
            newColor && this.props.onChange(newColor, changeType);
          }
        : undefined
    );
  };

  onBChange = (value: number, changeType: OnChangeMeta) => {
    this.setState(
      {b: Number(value)},
      this.props.onChange
        ? () => {
            const newColor = parseColor({
              ...toRgb(hsvToString(this.props.value)),
              b: this.state.b,
            });
            newColor && this.props.onChange(newColor, changeType);
          }
        : undefined
    );
  };

  render() {
    const state = this.getState();

    return (
      <>
        <NumberInput
          value={state.r}
          min={0}
          max={255}
          step={1}
          onChange={this.onRChange}
        />
        <NumberInput
          value={state.g}
          min={0}
          max={255}
          step={1}
          onChange={this.onGChange}
        />
        <NumberInput
          value={state.b}
          min={0}
          max={255}
          step={1}
          onChange={this.onBChange}
        />
      </>
    );
  }
}

class HSLInputs extends React.PureComponent<HSVColorPickerPropType> {
  onHChange = (value: number, changeType: OnChangeMeta) => {
    const hsl = hsvToHsl(this.props.value);
    this.props.onChange(hslToHsv({...hsl, h: Number(value)}), changeType);
  };

  onSChange = (value: number, changeType: OnChangeMeta) => {
    const hsl = hsvToHsl(this.props.value);
    this.props.onChange(hslToHsv({...hsl, s: Number(value) / 100}), changeType);
  };

  onLChange = (value: number, changeType: OnChangeMeta) => {
    const hsl = hsvToHsl(this.props.value);
    this.props.onChange(hslToHsv({...hsl, l: Number(value) / 100}), changeType);
  };

  render() {
    const {h, s, l} = hsvToHsl(this.props.value);
    return (
      <>
        <NumberInput
          data-automation-id="hsla-hue-input"
          value={Math.round(h)}
          min={0}
          max={359}
          step={1}
          onChange={this.onHChange}
        />
        <NumberInput
          data-automation-id="hsla-sat-input"
          value={Math.round(s * 100)}
          min={0}
          max={100}
          step={1}
          onChange={this.onSChange}
        />
        <NumberInput
          data-automation-id="hsla-lightness-input"
          value={Math.round(l * 100)}
          min={0}
          max={100}
          step={1}
          onChange={this.onLChange}
        />
      </>
    );
  }
}

class AInput extends React.PureComponent<HSVColorPickerPropType> {
  onChange = (value: number, changeType: OnChangeMeta) => {
    this.props.onChange(
      {
        ...this.props.value,
        a: Number(value) / 100,
      },
      changeType
    );
  };

  render() {
    const {a} = this.props.value;
    return (
      <NumberInput
        data-automation-id="alpha-input"
        value={Math.round(a * 100)}
        min={0}
        max={100}
        step={1}
        onChange={this.onChange}
      />
    );
  }
}

type ColorInputsState = {
  mode: 'hsb' | 'rgb' | 'hsl';
};

class ColorInputs extends React.PureComponent<
  HSVColorPickerPropType,
  ColorInputsState
> {
  state: ColorInputsState = {mode: 'hsb'};
  onToggleInputs = () => {
    this.setState(({mode}) => ({
      mode: mode === 'hsb' ? 'rgb' : mode === 'rgb' ? 'hsl' : 'hsb',
    }));
  };

  render() {
    const {onChange, value} = this.props;
    const {mode} = this.state;

    return (
      <InputGrid>
        <HexInput
          formatValue={getHex}
          onColorChange={onChange}
          value={hsvToString(value)}
        />

        <RGBHSBGrid>
          {mode === 'rgb' ? (
            <RGBInputs value={value} onChange={onChange} />
          ) : mode === 'hsl' ? (
            <HSLInputs value={value} onChange={onChange} />
          ) : (
            <HSBInputs value={value} onChange={onChange} />
          )}
        </RGBHSBGrid>

        <AInput value={value} onChange={onChange} />

        <TextCopy>HEX</TextCopy>

        <RGBHSBButton onClick={this.onToggleInputs}>
          <View width="100%" display="flex" justifyContent="space-around">
            <TextCopy>{mode === 'rgb' ? 'R' : 'H'}</TextCopy>
            <TextCopy>{mode === 'rgb' ? 'G' : 'S'}</TextCopy>
            <TextCopy>
              {mode === 'rgb' ? 'B' : mode === 'hsl' ? 'L' : 'B'}
            </TextCopy>
          </View>
        </RGBHSBButton>

        <TextCopy>A</TextCopy>
      </InputGrid>
    );
  }
}

/*
 * ----------------------------------------------------------------------------
 * Eyedropper Button
 * ----------------------------------------------------------------------------
 */

const EyedropperWrapper = emotionStyled('div')({
  placeSelf: 'stretch',
  gridColumn: '1 / 2',
  gridRow: '1 / 3',
});

const EyedropperButton = styled(Button)({
  paddingLeft: 0,
  paddingRight: 0,
  height: '100%',
});

type EyedropperProps = {
  onChange: OnChange;
};

class Eyedropper extends React.PureComponent<EyedropperProps> {
  onEyedropperClick = () => {
    if (!showEyeDropper()) {
      return;
    }

    // @ts-expect-error - TS2339 - Property 'EyeDropper' does not exist on type 'Window & typeof globalThis'.
    const eyeDropper = new window.EyeDropper();

    eyeDropper
      .open()
      // @ts-expect-error - TS7006 - Parameter 'result' implicitly has an 'any' type.
      .then((result) => {
        this.props.onChange(result.sRGBHex, {changeType: 'change'});
      })
      // Do nothing when users abort color selection
      .catch(noop);
  };

  render() {
    return (
      <TooltipTrigger alternate content="Pick a color">
        <EyedropperWrapper>
          <Hitarea>
            <EyedropperButton onClick={this.onEyedropperClick}>
              <EyedropperMediumIcon />
            </EyedropperButton>
          </Hitarea>
        </EyedropperWrapper>
      </TooltipTrigger>
    );
  }
}

export const ColorPicker = React.memo<ColorPickerPropType>(function ({
  value,
  onChange: _onChange,
  analyzeColorAccessibility,
  showColorContrastLines,
  getNativeElement,
}) {
  const rawHsv = getHSVA(value);

  // Hue is undefined for achromatic colors (black/white/grey all have s === 0).
  // Formats that can't carry it — hex/rgb/named/transparent — collapse it to 0
  // (red) on every round-trip. That desynced the two halves of the picker:
  // dragging the hue bar on a hueless color produces the same hex back, so the
  // bar kept its dragged hue while the saturation/brightness box re-derived
  // h = 0 and rendered the red gradient. Remember the last hue we saw so both
  // halves agree and a hue drag is visible even before the user raises
  // saturation/brightness.
  //
  // Only bridge when the hue was actually lost. `getHSVA` (via `colorToHSL`)
  // preserves an achromatic color's hue only for `hsla(...)` strings (its
  // `indexOf('hsla') === 0` branch); every other format — hex/rgb/named/
  // transparent, and even `hsl(...)` without alpha — routes through tinycolor,
  // which collapses a 0-saturation color to h = 0. Mirror that gate by format so
  // an explicit `hsla(0, 0%, …)` keeps its literal 0° hue, and don't lean on
  // `h === 0` (a real, preserved hue can be 0).
  const valuePreservesHue =
    typeof value === 'string' && value.indexOf('hsla') === 0;
  const [rememberedHue, setRememberedHue] = React.useState<number | null>(null);
  const hsv =
    rawHsv.s === 0 && !valuePreservesHue && rememberedHue != null
      ? {...rawHsv, h: rememberedHue}
      : rawHsv;

  // Keep the remembered hue current for chromatic values that arrive via props
  // (swatch pick, hex paste, initial mount) — not just live edits below.
  React.useEffect(() => {
    if (rawHsv.s !== 0 && rawHsv.h !== rememberedHue) {
      setRememberedHue(rawHsv.h);
    }
  }, [rawHsv.s, rawHsv.h, rememberedHue]);

  const onChange: OnChange = React.useCallback(
    (val, meta) => {
      // The slider/box hand us a full HSVA — capture its hue before hsvToString
      // flattens it away when the result is achromatic.
      if (typeof val === 'object' && Number.isFinite(val.h)) {
        setRememberedHue(val.h);
      }
      const newValue = hsvToString(
        typeof val === 'object' ? val : getHSVA(val)
      );
      _onChange(newValue, meta);
    },
    [_onChange]
  );

  return (
    <Locator>
      {/* @ts-expect-error - TS2739 - Type 'Record<string, any>' is missing the following properties from type 'Box': top, left, width, height */}
      {(ref, anchor = emptyObject) => (
        <ColorPickerGrid ref={ref}>
          <ColorBox
            value={hsv}
            width={anchor.width}
            onChange={onChange}
            getNativeElement={getNativeElement}
            showContrastLines={
              analyzeColorAccessibility && showColorContrastLines
            }
          />

          <HueOpacityGrid>
            {showEyeDropper() && <Eyedropper onChange={onChange} />}

            <HueSlider value={hsv} width={anchor.width} onChange={onChange} />

            <OpacitySlider
              value={hsv}
              width={anchor.width}
              onChange={onChange}
            />
          </HueOpacityGrid>

          <Divider />

          <ColorInputs value={hsv} onChange={onChange} />
        </ColorPickerGrid>
      )}
    </Locator>
  );
});

/*
 * ----------------------------------------------------------------------------
 * Swatches
 * ----------------------------------------------------------------------------
 */

const SWATCH_SIZE = 24;
const Swatch = styled(Clickable)(
  ({theme: {control, TextInput}, color, disabled}) => ({
    pointerEvents: disabled ? 'none' : 'auto',
    opacity: disabled ? 0.3 : 1,
    position: 'relative',
    boxSizing: 'border-box',
    width: SWATCH_SIZE,
    height: SWATCH_SIZE,
    borderStyle: 'solid',
    borderColor: TextInput.borderColor,
    borderWidth: control.borderWidth,
    borderRadius: control.borderRadius,
    background: isPartiallyTransparent(color)
      ? generateLightCheckeredBackgroundImage(color)
      : isTransparent(color)
        ? designerTheme.colors.foreground
        : color,
    color: isTransparent(color)
      ? designerTheme.colors.backgroundDark
      : isPartiallyTransparent(color)
        ? designerTheme.colors.foreground
        : 'currentColor',
    overflow: 'hidden',
  }),
  {
    selected: ({theme: {shadows}}) => ({
      boxShadow: shadows.selected,
    }),
    keyboardFocus: ({theme: {focusColors}}) => ({
      boxShadow: focusColors.outline,
      outline: 0,
    }),
  }
);

const NIB_SIZE = 10;

const CornerNibIcon = styled(CornerNib)({
  position: 'absolute',
  color: 'white',
  right: 0,
  bottom: 0,
  width: NIB_SIZE,
  height: NIB_SIZE,
});

const SwatchGridScrollView = styled(EnhancedScrollView)(
  ({theme: {gutters}}) => ({
    maxHeight: 120,
    // SwatchesGrid has a padding to offset this
    margin: -gutters.x4,
  })
);
const SwatchesGrid = styledDiv(({theme: {gutters}}) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(8, 1fr)',
  gridGap: gutters.x4,
  padding: gutters.x4,
}));

const IconWrapper = styled(Clickable)(({theme: {colors}, hover, disabled}) => ({
  opacity: disabled ? 0.3 : 1,
  cursor: disabled ? 'not-allowed' : 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  placeSelf: 'stretch',
  width: SWATCH_SIZE,
  height: SWATCH_SIZE,
  color: hover && !disabled ? colors.foregroundVivid : colors.foregroundDim,
}));

const SwatchViewGridForVariables = emotionStyled('div')({
  display: 'grid',
  // The 0px column is just a spacer
  gridTemplateColumns: `${SWATCH_SIZE}px 0px 1fr auto`,
  gridColumnGap: '$2',
  alignItems: 'center',
});

const ColorNameText = styled(Copy)({
  alignSelf: 'center',
  ...oneLineText,
});

const Grid = styledDiv(({theme: {gutters}}) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridRowGap: gutters.x4 * 2,
  padding: gutters.x8,
  userSelect: 'none',
}));

const colorIsTransparent = (swatch: SwatchType) =>
  !isValidColor(swatch.previewColor || swatch.color) ||
  isTransparent(swatch.previewColor || swatch.color);

export const eventChangeTypes = {
  COLOR_CHANGED: 'COLOR_CHANGED' as const,
  SWATCH_ADDED: 'SWATCH_ADDED' as const,
  SWATCH_REORDERED: 'SWATCH_REORDERED' as const,
  SWATCH_DELETED: 'SWATCH_DELETED' as const,
  SWATCH_SELECTED: 'SWATCH_SELECTED' as const,
  SWATCH_EDITING: 'SWATCH_EDITING' as const,
  SWATCH_EDIT_SAVED: 'SWATCH_EDIT_SAVED' as const,
  SWATCH_EDIT_CANCELLED: 'SWATCH_EDIT_CANCELLED' as const,
  SWATCH_ADD_CANCELLED: 'SWATCH_ADD_CANCELLED' as const,
  SWATCH_DELETE_CANCELLED: 'SWATCH_DELETE_CANCELLED' as const,
  SWATCH_CONVERT_CANCELLED: 'SWATCH_CONVERT_CANCELLED' as const,
  SWATCH_UNLINKED: 'SWATCH_UNLINKED' as const,
  SWATCH_EDIT_BEGAN: 'SWATCH_EDIT_BEGAN' as const,
} as const;

const defaultSwatchPanelState = {
  selectedSwatchId: null,
  __HACK__lastCreatedId: null,
  inputValue: '',
  adding: false,
  deleting: false,
  editing: false,
} as const;

const StyledDarkCheckerboardIcon = styled(DarkCheckerboardIcon)(() => ({
  position: 'relative',
  top: -1,
  left: -1,
}));

export const getSwatchPanelInitialState = (
  defaultState?: SwatchPanelState
): SwatchPanelState => defaultState || defaultSwatchPanelState;

export type SwatchPanelState = {
  selectedSwatchId: null | string;
  /**
   * Track the ID of our most-recently-created swatch; useful to avoid
   * race-condition traps that can occur when this newly-created swatch has not
   * made its way into `props.swatches` yet.
   *
   * A better fix might be to use `getDerivedStateFromProps` and copy incoming
   * props into `state`, and when creating new swatches just manually add it to
   * our state (again overwriting the state after the incoming `props.swatches`
   * updates)
   */
  __HACK__lastCreatedId: null | string;
  inputValue: string;
  adding: boolean;
  deleting: boolean;
  editing: boolean;
};

export type Meta = {
  type: keyof typeof eventChangeTypes;
  swatch: null | SwatchType;
  changeType: 'changeStart' | 'change' | 'changeEnd' | 'changeCancel';
};

type SwatchesProps = {
  onChange: (
    arg1: {
      swatches: SwatchType[];
      value: ColorValue;
    },
    meta: Meta
  ) => void;
  defaultState?: SwatchPanelState;
  editAccess?: boolean;
  getTooltipContentDisabled: (
    arg1: 'createSwatch' | 'editSwatch' | 'deleteSwatch'
  ) => string;
  swatches: SwatchType[];
  value: ColorValue;
  analyzeColorAccessibility?: boolean;
  getNativeElement: () => HTMLElement | null;
  showColorContrastLines: boolean;
  toggleColorContrastLines: () => void;
  roleCanEditSwatches: boolean;
  canViewVariablesPanel?: boolean;
  hideSwatchSelector?: boolean;
  openVariablePanel?: () => void;
};

const transparentSwatch = {
  id: 'transparent',
  name: 'Transparent',
  previewColor: '',
  color: 'transparent',
} as const;

const getFontSizeAndWeightFromElement = (
  element: HTMLElement
): Array<number> => {
  const ret: Array<number> = [];
  try {
    const {fontSize, fontWeight} = window.getComputedStyle(element);
    const size = parseFloat(fontSize);
    const weight = parseInt(fontWeight, 10);
    ret[0] = size;
    ret[1] = weight;
  } catch (e: any) {
    ret[0] = NaN;
    ret[1] = NaN;
  }

  return ret;
};

const getWcagFontSizeFromElement = (
  element: HTMLElement
): 'large' | 'small' => {
  const [size, weight] = getFontSizeAndWeightFromElement(element);
  // @ts-expect-error - TS18048 - 'weight' is possibly 'undefined'.
  return weight >= 700
    ? // @ts-expect-error - TS18048 - 'size' is possibly 'undefined'.
      size >= 18.5
      ? 'large'
      : 'small'
    : // @ts-expect-error - TS18048 - 'size' is possibly 'undefined'.
      size >= 24
      ? 'large'
      : 'small';
};

const getBackgroundColorFromElement = (element: HTMLElement) => {
  const white = 'rgb(255, 255, 255)';

  if (!element) {
    return white;
  }

  try {
    let el = element;
    let isOpaque = false;
    const bgColors: Array<any | string> = [];

    // gather all visible colors
    while (el && !isOpaque && !/^html$/i.test(el.tagName)) {
      const {backgroundColor} = window.getComputedStyle(el);
      bgColors.push(backgroundColor);
      isOpaque = tinycolor(backgroundColor).getAlpha() === 1;
      if (/^body$/i.test(el.tagName) && !isOpaque) {
        // semi-opaque body elements have a white background
        bgColors.push(white);
      }
      // @ts-expect-error - TS2322 - Type 'HTMLElement | null' is not assignable to type 'HTMLElement'.
      el = el.parentElement;
    }

    // calculate visible color based on opacities of stacked colors
    return bgColors.reduce(blendColors);
  } catch {
    // guess that the background is white
    return white;
  }
};

const ColorTile = styled('span')(({theme, color}) => ({
  display: 'inline-block',
  border: `1px solid ${theme.colors.border}`,
  borderRadius: theme.control.borderRadius,
  backgroundColor: color,
  height: 11,
  width: 11,
  marginBottom: -2,
  marginRight: 3,
}));

const HideButton = styled(Link)(({theme}) => ({
  height: theme.gutters.x16,
}));

const EyeButton = styled('button')(
  ({theme: {control, colors, gutters, focusColors}, hover, keyboardFocus}) => ({
    width: gutters.x20,
    height: gutters.x20,
    backgroundColor: hover ? colors.backgroundLight : 'transparent',
    color: hover ? colors.foreground : colors.foregroundDim,
    borderRadius: control.borderRadius,
    paddingLeft: 0,
    paddingRight: 0,
    borderWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: gutters.x8,
    outline: 'none',
    boxShadow: keyboardFocus ? focusColors.outline : null,
  })
);

const HelpButton = styled('button')(
  ({theme: {colors, gutters, focusColors}, hover, keyboardFocus}) => ({
    width: gutters.x12,
    height: gutters.x12,
    backgroundColor: 'transparent',
    color: hover ? colors.foregroundDim : colors.foregroundDimmer,
    paddingLeft: 0,
    paddingRight: 0,
    borderWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: gutters.x4,
    outline: 'none',
    boxShadow: keyboardFocus ? focusColors.outline : null,
  })
);

const ColorContrastBadge = styled('span')(({fail, theme}) => ({
  color: fail ? theme.colors.dangerAccent : theme.colors.successAccent,
  fontWeight: 'bold',
  marginLeft: 'auto',
  marginRight: 6,
}));

const ColorContrastRatioSection = ({
  currentColor,
  getNativeElement,
  showColorContrastLines,
  toggleColorContrastLines,
}: {
  currentColor: string;
  getNativeElement: () => HTMLElement | null;
  showColorContrastLines: boolean;
  toggleColorContrastLines: () => void;
}) => {
  const element = React.useMemo(() => getNativeElement(), [getNativeElement]);
  const [isVisible, setVisible] = React.useState(false);
  const toggleVisibility = React.useCallback(() => {
    setVisible((wasVisible) => !wasVisible);
  }, [setVisible]);

  if (element == null) return null;

  const backgroundColor = getBackgroundColorFromElement(element);
  const wcagFontSize = getWcagFontSizeFromElement(element);
  const [fontSize, fontWeight] = getFontSizeAndWeightFromElement(element);
  const blendedForegroundColor = blendColors(currentColor, backgroundColor);
  const isAAA = tinycolor.isReadable(blendedForegroundColor, backgroundColor, {
    level: 'AAA',
    size: wcagFontSize,
  });
  const isAA = tinycolor.isReadable(blendedForegroundColor, backgroundColor, {
    level: 'AA',
    size: wcagFontSize,
  });
  return (
    <>
      <FlexRow align="center">
        Contrast ratio
        <Hitarea>
          <HelpButton
            type="button"
            onClick={toggleVisibility}
            aria-label="Show contrast ratio help"
            data-automation-id="color-contrast-toggle-help"
          >
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-expect-error */}
            <HelpCircleIcon size={11} />
          </HelpButton>
        </Hitarea>
        <ColorContrastBadge
          fail={!isAAA && !isAA}
          data-automation-id="color-contrast-badge"
        >
          {isAAA ? 'AAA' : isAA ? 'AA' : 'Fail'}
        </ColorContrastBadge>
        <span
          style={{minWidth: '4.25ch', textAlign: 'right'}}
          data-automation-id="color-contrast-ratio"
        >
          {tinycolor
            .readability(blendedForegroundColor, backgroundColor)
            .toFixed(2)}
        </span>
        <Hitarea>
          <EyeButton
            type="button"
            aria-label="Show contrast ratio lines"
            onClick={toggleColorContrastLines}
            data-automation-id="color-contrast-toggle-lines"
          >
            {showColorContrastLines ? <HideIcon /> : <ShowIcon />}
          </EyeButton>
        </Hitarea>
      </FlexRow>

      {isVisible && (
        <Note
          data-automation-id="color-contrast-help"
          colorVariant="default"
          hasIcon={false}
        >
          <>
            <span>
              Contrast ratio impacts your site&apos;s accessibility. Sufficient
              contrast ratio depends on foreground{' '}
              <ColorTile color={currentColor} /> and background{' '}
              <ColorTile color={backgroundColor} /> colors, font size (
              {/* @ts-expect-error - TS18048 - 'fontSize' is possibly 'undefined'. */}
              {Number.isInteger(fontSize) ? fontSize : fontSize.toFixed(1)}
              px), and font weight ({fontWeight}).
            </span>
            <HStack align="center" spacing={1}>
              <NoteLink
                href="https://university.webflow.com/lesson/make-your-site-more-accessible#apply-recommended-color-contrast-ratios"
                isExternal
                target="_blank"
                rel="noopener"
              >
                Learn more
              </NoteLink>
              <HideButton onClick={toggleVisibility}>Hide this</HideButton>
            </HStack>
          </>
        </Note>
      )}
    </>
  );
};

const Icon = ({
  // @ts-expect-error - TS7031 - Binding element 'onClick' implicitly has an 'any' type.
  onClick,
  // @ts-expect-error - TS7031 - Binding element 'tooltipContent' implicitly has an 'any' type.
  tooltipContent,
  // @ts-expect-error - TS7031 - Binding element 'tooltipContentDisabled' implicitly has an 'any' type.
  tooltipContentDisabled,
  isRestricted = false,
  // @ts-expect-error - TS7031 - Binding element 'children' implicitly has an 'any' type.
  children,
  // @ts-expect-error - TS7031 - Binding element 'roleCanEditSwatches' implicitly has an 'any' type.
  roleCanEditSwatches,
}) => {
  return (
    <TooltipTrigger
      content={
        isRestricted && !roleCanEditSwatches
          ? tooltipContentDisabled
          : tooltipContent
      }
      alternate
    >
      <Hitarea>
        <IconWrapper
          disabled={isRestricted && !roleCanEditSwatches}
          onClick={onClick}
        >
          {children}
        </IconWrapper>
      </Hitarea>
    </TooltipTrigger>
  );
};

export class ColorSwatchPanel extends React.PureComponent<
  SwatchesProps,
  SwatchPanelState
> {
  state = getSwatchPanelInitialState(this.props.defaultState);

  _onChangeCallback = (
    swatches: SwatchType[],
    value: ColorValue,
    meta: Meta
  ) =>
    this.props.onChange
      ? () => {
          this.props.onChange({swatches, value}, meta);
        }
      : undefined;

  _getColorValue = (color: ColorString): ColorValue => {
    const {value} = this.props;
    return value.type === 'color' ? {...value, value: color} : value;
  };

  _getSwatch = (swatchId: string): SwatchType | undefined => {
    /**
     * This allows us to skip error logging during the race-condition where our
     * newly-created swatch has not yet made its way into `props.swatches`
     *
     * (Fixes #28525)
     */
    const __HACK__skipReportBugIfNotFound =
      swatchId === this.state.__HACK__lastCreatedId;

    const swatch = this.props.swatches.find(({id}) => id === swatchId);

    if (
      !__HACK__skipReportBugIfNotFound &&
      swatchId !== 'transparent' &&
      !swatch
    ) {
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug(
        'SwatchNotFound',
        `Could not find swatch with ID ${JSON.stringify(
          this.state.selectedSwatchId
        )}`,
        {
          'WebflowColorPicker::state': this.state,
        }
      );
    }

    return swatch;
  };

  getSelectedSwatch = (): SwatchType | undefined => {
    return this.state.selectedSwatchId != null
      ? this._getSwatch(this.state.selectedSwatchId)
      : undefined;
  };

  onInputChange = (e: React.ChangeEvent<EventTarget>) => {
    // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'EventTarget'.
    this.setState({inputValue: e.target.value});
  };

  onEnter = (e: React.KeyboardEvent<EventTarget>) => {
    if (e.key === 'Enter') {
      this.onSave();
    }
  };

  selectSwatch = weakMemo((swatch: SwatchType) => () => {
    const meta = {
      type: eventChangeTypes.SWATCH_SELECTED,
      changeType: 'change',
      convertType: null,
      swatch,
    } as const;

    this.setState(
      {
        selectedSwatchId: swatch.id,
      },
      this._onChangeCallback(
        this.props.swatches,
        swatch === transparentSwatch
          ? {type: 'color', value: 'transparent'}
          : {type: 'global-swatch-reference', id: swatch.id},
        meta
      )
    );
  });

  openAddSwatch = () => {
    this.setState({
      adding: true,
      selectedSwatchId: null,
      inputValue: humanizeNamedColor(closestColorName(this.getCurrentColor())),
    });
  };

  openEditSwatch = () => {
    if (this.state.selectedSwatchId == null) {
      return;
    }
    const swatch = this._getSwatch(this.state.selectedSwatchId);
    if (swatch == null) {
      return;
    }
    this.setState(
      {editing: true, inputValue: swatch.name},
      this._onChangeCallback(
        this.props.swatches,
        {type: 'global-swatch-reference', id: swatch.id},
        {
          type: eventChangeTypes.SWATCH_EDIT_BEGAN,
          changeType: 'changeStart',
          swatch,
        }
      )
    );
  };

  openDeleteSwatch = () => {
    if (this.state.selectedSwatchId == null) {
      return;
    }
    const swatch = this._getSwatch(this.state.selectedSwatchId);
    if (swatch == null) {
      return;
    }

    this.setState({deleting: true});
  };

  unlinkSwatch = () => {
    if (this.state.selectedSwatchId == null) {
      return;
    }
    const swatch = this._getSwatch(this.state.selectedSwatchId);
    if (swatch == null) {
      return;
    }

    const meta = {
      type: eventChangeTypes.SWATCH_UNLINKED,
      changeType: 'changeEnd',
      swatch,
    } as const;

    const value = {type: 'color', value: this.getCurrentColor()} as const;

    this.setState(
      {
        selectedSwatchId: null,
      },
      this._onChangeCallback(this.props.swatches, value, meta)
    );
  };

  onCancel = () => {
    const prevState = this.state;
    const {swatches, value} = this.props;
    const selectedSwatch = this.getSelectedSwatch();

    this.setState(
      {
        adding: false,
        editing: false,
        deleting: false,
        inputValue: '',
      },
      this._onChangeCallback(
        cancelSwatchEdit(swatches),
        prevState.editing && selectedSwatch
          ? this._getColorValue(selectedSwatch.color)
          : value,
        {
          type: prevState.deleting
            ? eventChangeTypes.SWATCH_DELETE_CANCELLED
            : prevState.editing
              ? eventChangeTypes.SWATCH_EDIT_CANCELLED
              : eventChangeTypes.SWATCH_ADD_CANCELLED,
          changeType: 'change',
          swatch:
            prevState.editing || prevState.deleting
              ? swatches.find(({id}) => id === prevState.selectedSwatchId) ||
                null
              : null,
        }
      )
    );
  };

  onSave = () => {
    const prevState = this.state;
    const {value} = this.props;
    let {swatches} = this.props;

    let newSwatch;

    if (prevState.adding) {
      newSwatch = createNewSwatch(
        this.getCurrentColor(),
        prevState.inputValue,
        generateSwatchGuid()
      );

      swatches = swatches.concat(newSwatch);
    }

    if (prevState.editing && prevState.selectedSwatchId) {
      swatches = saveSwatchEdit(swatches, {
        id: prevState.selectedSwatchId,
        name: prevState.inputValue,
      });
    }

    this.setState(
      {
        adding: false,
        editing: false,
        inputValue: '',
        selectedSwatchId: newSwatch ? newSwatch.id : prevState.selectedSwatchId,
        __HACK__lastCreatedId: newSwatch ? newSwatch.id : null,
      },
      this._onChangeCallback(swatches, value, {
        type: prevState.adding
          ? eventChangeTypes.SWATCH_ADDED
          : eventChangeTypes.SWATCH_EDIT_SAVED,
        changeType: 'change',
        swatch: newSwatch
          ? newSwatch
          : swatches.find(({id}) => id === this.state.selectedSwatchId) || null,
      })
    );
  };

  onDeleteSave = () => {
    const {swatches, value} = this.props;
    const prevState = this.state;
    const swatch = this.getSelectedSwatch();
    if (swatch == null || prevState.selectedSwatchId === null) {
      return;
    }

    this.setState(
      {selectedSwatchId: null, deleting: false},
      this._onChangeCallback(
        deleteSwatch(swatches, prevState.selectedSwatchId),
        value,
        {
          type: eventChangeTypes.SWATCH_DELETED,
          changeType: 'change',
          swatch,
        }
      )
    );
  };

  getCurrentColor = (): ColorString => {
    const {editing, selectedSwatchId} = this.state;

    if (selectedSwatchId && editing) {
      // TODO // let swatch crash if not found, but fix undo/redo here
      const {previewColor, color} = this._getSwatch(selectedSwatchId) || {};
      // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
      return previewColor || color;
    }

    const {value} = this.props;

    if (value.type === 'color') {
      return value.value;
    }

    if (value.type === 'ref') {
      throw new Error(
        'WebflowColorPicker: VariableReference not yet supported'
      );
    }

    // TODO // let swatch crash if not found, but fix undo/redo here
    const {previewColor, color} = this._getSwatch(value.id) || {};

    // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
    return previewColor || color;
  };

  onColorChange = (
    value: ColorString | HSVA,
    {changeType}: OnChangeMeta = changeRef
  ) => {
    const color = typeof value === 'string' ? value : hsvToString(value);
    const {editing, selectedSwatchId} = this.state;
    const {swatches} = this.props;

    const meta = {
      type: editing
        ? eventChangeTypes.SWATCH_EDITING
        : eventChangeTypes.COLOR_CHANGED,
      swatch: selectedSwatchId
        ? swatches.find(({id}) => id === selectedSwatchId) || null
        : null,
      changeType,
    } as const;

    this.setState(
      (state) => {
        if (
          state.selectedSwatchId &&
          !state.editing &&
          !state.adding &&
          !state.deleting
        ) {
          return {selectedSwatchId: null};
        }

        return null;
      },
      this._onChangeCallback(
        editing && selectedSwatchId
          ? changeSwatchColor(swatches, {
              id: selectedSwatchId,
              color,
            })
          : swatches,
        editing ? this._getColorValue(color) : {type: 'color', value: color},
        meta
      )
    );
  };

  render() {
    const {
      swatches,
      editAccess = true,
      getTooltipContentDisabled,
      analyzeColorAccessibility,
      getNativeElement,
      showColorContrastLines,
      toggleColorContrastLines,
      roleCanEditSwatches,
      hideSwatchSelector = false,
      openVariablePanel,
      canViewVariablesPanel,
    } = this.props;
    const {selectedSwatchId, adding, editing, deleting} = this.state;
    const selectedSwatch = this.getSelectedSwatch();
    const currentColor = this.getCurrentColor();

    return (
      <Grid
        data-legacy-ds="WebflowColorPicker"
        data-automation-id="color-select-panel"
      >
        <ColorPicker
          onChange={this.onColorChange}
          value={currentColor}
          analyzeColorAccessibility={analyzeColorAccessibility}
          showColorContrastLines={showColorContrastLines}
          getNativeElement={getNativeElement}
        />

        {analyzeColorAccessibility && (
          <>
            <Divider />
            <ColorContrastRatioSection
              getNativeElement={getNativeElement}
              showColorContrastLines={showColorContrastLines}
              toggleColorContrastLines={toggleColorContrastLines}
              currentColor={currentColor}
            />
          </>
        )}
        {!hideSwatchSelector && (
          <SwatchGridScrollView>
            <SwatchesGrid>
              <TooltipTrigger content="Transparent" alternate>
                <div>
                  <PseudoProvider>
                    {({hover}) => (
                      <Swatch
                        hover={hover}
                        disabled={
                          adding ||
                          (editing && selectedSwatchId !== 'transparent')
                        }
                        selected={
                          selectedSwatch && selectedSwatch.id === 'transparent'
                        }
                        onClick={this.selectSwatch(transparentSwatch)}
                        color="transparent"
                      >
                        <StyledDarkCheckerboardIcon />
                      </Swatch>
                    )}
                  </PseudoProvider>
                </div>
              </TooltipTrigger>

              {swatches.map((swatch) => (
                <TooltipTrigger key={swatch.id} content={swatch.name} alternate>
                  <div>
                    <PseudoProvider>
                      {({hover}) => (
                        <Swatch
                          data-automation-id="swatch"
                          hover={hover}
                          disabled={
                            adding ||
                            (editing && selectedSwatchId !== swatch.id)
                          }
                          selected={selectedSwatchId === swatch.id}
                          onClick={this.selectSwatch(swatch)}
                          color={swatch.previewColor || swatch.color}
                        >
                          {editAccess ? <CornerNibIcon /> : null}

                          {colorIsTransparent(swatch) && (
                            <StyledDarkCheckerboardIcon />
                          )}
                        </Swatch>
                      )}
                    </PseudoProvider>
                  </div>
                </TooltipTrigger>
              ))}
              {editAccess &&
                (adding ? (
                  <Swatch color={currentColor}>
                    <CornerNibIcon />
                  </Swatch>
                ) : (
                  !editing &&
                  !deleting && (
                    <Icon
                      onClick={this.openAddSwatch}
                      tooltipContent={
                        <Translation>
                          {(t) => t('designer.tokens.color.add')}
                        </Translation>
                      }
                      tooltipContentDisabled={getTooltipContentDisabled(
                        'createSwatch'
                      )}
                      isRestricted
                      roleCanEditSwatches={roleCanEditSwatches}
                    >
                      <AddIcon />
                    </Icon>
                  )
                ))}
            </SwatchesGrid>
          </SwatchGridScrollView>
        )}

        <StyleVariableInfo
          selectedSwatchId={selectedSwatchId}
          selectedSwatch={selectedSwatch}
          currentColor={currentColor}
          roleCanEditSwatches={roleCanEditSwatches}
          openVariablePanel={openVariablePanel}
          canViewVariablesPanel={canViewVariablesPanel}
        />
      </Grid>
    );
  }
}

type StyleVariableInfoProps = {
  selectedSwatchId: string | null;
  selectedSwatch?: SwatchType;
  currentColor: string;
  roleCanEditSwatches?: boolean;
  openVariablePanel?: () => void;
  canViewVariablesPanel?: boolean;
};

const StyleVariableInfo = ({
  selectedSwatchId,
  selectedSwatch,
  currentColor,
  roleCanEditSwatches,
  openVariablePanel,
  canViewVariablesPanel,
}: StyleVariableInfoProps) => {
  if (!selectedSwatchId || !selectedSwatch || !canViewVariablesPanel) {
    return null;
  }

  return (
    <>
      <Divider />
      <SwatchViewGridForVariables>
        <Swatch color={currentColor}>
          {colorIsTransparent(selectedSwatch) && <StyledDarkCheckerboardIcon />}
        </Swatch>
        <div />
        <ColorNameText>{selectedSwatch.name}</ColorNameText>
        {selectedSwatchId && openVariablePanel && (
          <Icon
            onClick={openVariablePanel}
            tooltipContent={
              <Translation>
                {(t) => t('designer.tokens.panel.open')}
              </Translation>
            }
            tooltipContentDisabled=""
            roleCanEditSwatches={roleCanEditSwatches}
          >
            <ViewEdgesIcon />
          </Icon>
        )}
      </SwatchViewGridForVariables>
    </>
  );
};
